/**
 * Stats counter
 *
 * Counts each `.js-stat-count` up from 0 to its authored value once, on first
 * scroll into view. Free-text safe: the title can be currency, suffixed, grouped
 * or digit-free ("Award Winning"), in which case it's left untouched.
 *
 * The authored HTML value is the source of truth: we only overwrite it with the
 * start value once we've confirmed the stat will animate AND is in view, so with
 * JS off (or below the fold) the correct number is always on screen, never "0".
 * That ordering is load-bearing - don't reset earlier. reduced-motion bails out
 * entirely, leaving the authored value in place.
 */

const ANIMATION_DURATION = 1600;
const OBSERVER_THRESHOLD = 0.5;
const OBSERVER_ROOT_MARGIN = '0px 0px -10% 0px';

// First run of digits, with optional thousands-commas and one decimal. Grouped
// alternative comes first so "1,200" is captured whole, not just "1". A leading
// "-" stays in the prefix, so "-3%" counts its magnitude (0 -> 3) rather than
// counting down through negatives.
const NUMBER_RE = /\d{1,3}(?:,\d{3})+(?:\.\d+)?|\d+(?:\.\d+)?/;

const easeOut = (t) => 1 - (1 - t) ** 3;

const prefersReducedMotion = () =>
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Tokenise the title into prefix + number + suffix for the animator. Pure - never
 * mutates the DOM. `animates: false` means no countable digits, leave it alone.
 */
function parseStat(input) {
    const match = NUMBER_RE.exec(input);

    if (!match) {
        return {
            animates: false,
            prefix: input,
            suffix: '',
            number: null,
            decimals: 0,
            grouped: false,
            originalText: input,
        };
    }

    const rawToken = match[0];
    const index = match.index;

    // Text around the token rides along verbatim (currency, signs, units, words).
    const prefix = input.slice(0, index);
    const suffix = input.slice(index + rawToken.length);

    const grouped = rawToken.includes(',');
    const cleaned = rawToken.replace(/,/g, '');
    const decimalIdx = cleaned.indexOf('.');
    const decimals = decimalIdx === -1 ? 0 : cleaned.length - decimalIdx - 1;

    return {
        animates: true,
        prefix,
        suffix,
        number: Number(cleaned),
        decimals,
        grouped,
        originalText: input,
    };
}

// Format an intermediate frame, honouring the authored decimals and grouping.
// The final frame writes originalText back instead, so it isn't used there.
function formatNumber(value, decimals, grouped) {
    if (grouped) {
        return new Intl.NumberFormat('en-GB', {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals,
            useGrouping: true,
        }).format(value);
    }

    return value.toFixed(decimals);
}

export default class StatsCounter {
    constructor() {
        const els = document.querySelectorAll('.js-stat-count');

        if (!els.length) {
            return;
        }

        // reduced-motion: leave the authored value on screen, do nothing.
        if (prefersReducedMotion()) {
            return;
        }

        this.observer = new IntersectionObserver(
            (entries, observer) => {
                entries.forEach((entry) => {
                    if (!entry.isIntersecting) {
                        return;
                    }

                    observer.unobserve(entry.target);
                    this.animate(entry.target);
                });
            },
            {
                threshold: OBSERVER_THRESHOLD,
                rootMargin: OBSERVER_ROOT_MARGIN,
            }
        );

        els.forEach((el) => {
            const parsed = parseStat(el.textContent.trim());

            if (!parsed.animates) {
                return;
            }

            // Stash for the observer callback. Note we do NOT reset to the start
            // value here - that happens in animate(), once the stat is in view.
            el.statData = parsed;
            this.observer.observe(el);
        });
    }

    animate(el) {
        const { prefix, suffix, number, decimals, grouped, originalText } =
            el.statData;

        // Only now - in view, motion allowed - reset to the start value.
        el.textContent = prefix + formatNumber(0, decimals, grouped) + suffix;

        const start = performance.now();

        const tick = (now) => {
            const progress = Math.min((now - start) / ANIMATION_DURATION, 1);
            const value = number * easeOut(progress);

            if (progress < 1) {
                el.textContent =
                    prefix + formatNumber(value, decimals, grouped) + suffix;
                requestAnimationFrame(tick);
                return;
            }

            // Final frame: restore the exact authored string, not a recomputed one.
            el.textContent = originalText;
        };

        requestAnimationFrame(tick);
    }
}
